import React, { useCallback, useEffect } from 'react'
import { createContext, useContext, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { premmiar } from "../../Services/Apis/clients";
import { useNotifications } from "../../Shared/Notification";
import { formatDateToBRString } from "../../Utils/format";

const RatingContext = createContext({
    isLoading: false,
    isEnabled: false,
    myRating: null,
    configRating: null,
    ratingList: {},
    filterProductsWithoutRating: (items) => {
        throw new Error('Contexto não atribuido')
    },
    getRatingByProfile: (items) => {
        throw new Error('Contexto não atribuido')
    },
    getRatingBySku: (sku) => {
        throw new Error('Contexto não atribuido')
    }
})

export const useRating = () => useContext(RatingContext)
let listOfSkus = []
let checkUserRatingPendences = false
const RatingProvider = ({ children }) => {
    const Oidc = useSelector(({ oidc }) => oidc)
    const [isLoading, setIsloading] = useState(false)
    const [error, setError] = useState(null)
    const [isEnabled, setIsEnabled] = useState(false)
    const [myRating, setMyRating] = useState(null)
    const [ratingList, setRatingList] = useState({})
    const [configRating, setConfigRating] = useState(null)
    const { addNotification } = useNotifications()

    const addRatingInList = (index, data) => {
        const newList = {
            ...ratingList
        }
        newList[index] = data;
        // setRatingList({ ...newList });
    }

    const checkIfUserHasRatingsPending = useCallback(() => {
        const { user } = Oidc;
        if (!user || checkUserRatingPendences) return;
        checkUserRatingPendences = true;
        premmiar(user).get(`api/Rating/ParticipantHasPendingRatings`).then(({ data: { pending } }) => {
            if (pending) {
                addNotification('rating-pending', {
                    message: '',
                    read: false,
                    date: formatDateToBRString(new Date()),
                    title: 'Você tem avaliações pendentes.',
                    href: '/my-orders',
                    hrefText: 'Veja seus pedidos!'
                })
            }
        })
    }, [checkUserRatingPendences, Oidc, addNotification])

    const getRatingBySku = useCallback((sku) => {
        const { user } = Oidc;
        if (ratingList[sku] || listOfSkus.indexOf(sku) >= 0) return;
        listOfSkus.push(sku)
        premmiar(user).get(`api/RatingAverage/Average?identification=${sku}&type=1`).then(({ data }) => {
            if (data?.response?.code === 404) {
                addRatingInList(sku, {
                    rating: 0,
                    status: 404,
                    totalCount: 0,
                    comment: data?.response?.message
                });
            } else {
                addRatingInList(sku, {
                    rating: data.average,
                    status: 200,
                    totalCount: data.totalCount,
                    comment: 'success'
                });
            }

        }).catch((e) => {
            if (process.env.NODE_ENV === 'development') console.log({ [sku]: e })
            if (e?.response?.data?.code === 404) {
                addRatingInList(sku, {
                    rating: 0,
                    status: 404,
                    totalCount: 0,
                    comment: e?.response?.data?.message
                });
            }
        })
    }, [Oidc, ratingList])

    const filterProductsWithoutRating = useCallback((items = []) => {
        const itemList = [];
        const skus = (myRating || []).map(({ productSku, orderId }) => ({ productSku, orderId }))
        items.forEach((item) => {
            const { productSku, orderId } = item;
            const checkIfProductHasRating = skus.filter(({ productSku: pSku, orderId: oId }) => productSku === pSku && orderId === oId);
            if (checkIfProductHasRating.length === 0) {
                itemList.push(item);
            }
        })

        return itemList
    }, [myRating])

    const getRatingByProfile = useCallback(() => {
        const { user } = Oidc;
        setIsloading(true)
        premmiar(user).get(`api/Rating/Participant`).then(({ data }) => {
            const newList = {}
            setMyRating(data);
            data.forEach((item) => {
                newList[item.productSku] = item
            })
            setRatingList(newList)
            setIsEnabled(true)
            if (!checkUserRatingPendences) checkIfUserHasRatingsPending()
        }).catch((e) => {
            setError(e)
            setIsEnabled(false)
        }).finally(setIsloading(false))
    }, [isLoading, Oidc, error, isEnabled, checkUserRatingPendences])

    const checkIfRatingCampaign = useCallback(() => {
        const { user } = Oidc;
        const catalogId = process.env.REACT_APP_CATALOG_ID;
        premmiar(user).get(`api/CampaignCatalog/${catalogId}`).then(({ data }) => {
            setConfigRating(data)
            getRatingByProfile()
        }).catch((e) => {
            setError(e)
            setIsloading(false)
            setIsEnabled(false)
        })
    }, [isLoading, Oidc, error, isEnabled])

    const checkIfRatingCampaignIsActive = useCallback(() => {
        const { user } = Oidc;
        premmiar(user).get('api/CampaignCatalog/IsActive').then(({ data: { isActive } }) => {
            if (isActive) {
                setIsEnabled(true)
            }
            checkIfRatingCampaign()
        }).catch((e) => {
            setError(e)
            setIsloading(false)
            setIsEnabled(false)
        })
    }, [isLoading, Oidc, error, isEnabled])
    const value = useMemo(() => ({
        isLoading,
        myRating,
        filterProductsWithoutRating,
        getRatingByProfile,
        getRatingBySku,
        ratingList,
        configRating,
        isEnabled
    }), [
        isLoading,
        myRating,
        filterProductsWithoutRating,
        getRatingByProfile,
        getRatingBySku,
        ratingList,
        configRating,
        isEnabled
    ])

    useEffect(() => {
        if (Oidc?.user && Oidc?.user?.profile && Oidc?.user?.profile?.email && !myRating && !isLoading && !error) {
            setIsloading(true)
            checkIfRatingCampaignIsActive();
        }
    }, [Oidc, myRating, isLoading, error]);

    return (
        <RatingContext.Provider value={value}>
            {children}
        </RatingContext.Provider>
    )
}

export default RatingProvider;
